import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState } from 'react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
}

const ONBOARDING_CHECK_TIMEOUT_MS = 8000;

export function ProtectedRoute({ children, requireOnboarding = true }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;

    const checkOnboarding = async () => {
      // Default: done checking unless we explicitly start a request
      if (!user || !requireOnboarding) {
        if (!cancelled) {
          setOnboardingComplete(null);
          setCheckingOnboarding(false);
        }
        return;
      }

      if (!cancelled) setCheckingOnboarding(true);

      // Guard against requests that never resolve (rare, but causes an endless spinner)
      const queryPromise = Promise.resolve(
        supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .single()
      );

      const timeoutPromise = new Promise<{ data: null; error: { message: string } }>((resolve) => {
        timeoutId = window.setTimeout(
          () => resolve({ data: null, error: { message: 'timeout' } }),
          ONBOARDING_CHECK_TIMEOUT_MS
        );
      });

      try {
        const result = (await Promise.race([queryPromise, timeoutPromise])) as any;
        const { data, error } = result;

        if (cancelled) return;

        if (!error && data) {
          setOnboardingComplete(data.onboarding_completed);
        } else {
          // If we can't read the profile (RLS/network/etc), don't deadlock the UI.
          setOnboardingComplete(null);
        }
      } catch {
        if (!cancelled) setOnboardingComplete(null);
      } finally {
        if (timeoutId) window.clearTimeout(timeoutId);
        // Avoid potential unhandled rejection if the query finishes after timing out.
        void queryPromise.catch(() => undefined);
        if (!cancelled) setCheckingOnboarding(false);
      }
    };

    checkOnboarding();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [user, requireOnboarding]);

  if (loading || checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // If onboarding is required and not complete, redirect to onboarding
  if (requireOnboarding && onboardingComplete === false && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // If on onboarding page but already completed, redirect to app
  if (location.pathname === '/onboarding' && onboardingComplete === true) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}

